import type { MeasurementStrategyContext, PdpMeasurementStrategy } from './PdpMeasurementStrategy';

const GALLERY_CLICK_ROOT_SELECTOR = '.gallery-slider, .gallery-slider-item, .product-detail-media';

/**
 * PDP main-media clicks (gallery / zoom container): delegated `click` keeps one listener while themes
 * and gallery plugins reshuffle inner nodes; video slides may not use a bare `<img>` target.
 *
 * Plugin coverage: partial — works when the shop keeps Shopware’s gallery/media wrappers (e.g. Studio
 * Solid “Product videos in product gallery”, Picture Gallery Premium and similar extensions that still
 * nest under `.gallery-slider` / `.product-detail-media`). Lazyload wrappers (e.g. FroshLazySizes) are OK
 * if clicks bubble from inside those roots. Full gallery replacements with different outer markup need
 * selector updates or a separate strategy.
 */
export class GalleryImageClickStrategy implements PdpMeasurementStrategy {
  private ctx: MeasurementStrategyContext | null = null;

  private onClick: ((ev: Event) => void) | null = null;

  attach(ctx: MeasurementStrategyContext): void {
    this.ctx = ctx;
    const { events, store, document: doc } = ctx;

    this.onClick = (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target?.closest) {
        return;
      }
      if (target.closest(GALLERY_CLICK_ROOT_SELECTOR) != null) {
        store.incrementImageClicked();
      }
    };

    events.on(doc, 'click', this.onClick);
  }

  dispose(): void {
    if (this.onClick != null && this.ctx != null) {
      this.ctx.events.off(this.ctx.document, 'click', this.onClick);
    }
    this.onClick = null;
    this.ctx = null;
  }
}
